import { SiteLayout } from "@/components/layout/SiteLayout";
import { RelatedPages } from "@/components/RelatedPages";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, faqSchema, personSchema, webPageSchema } from "@/lib/seo";
import { EducationalDisclaimer } from "@/components/EducationalDisclaimer";
import { withNetworkUtm } from "@/lib/network";

export default function MediaInquiries() {
  const page = webPageSchema({
    path: "/media-inquiries",
    title: "Media & Speaking Inquiries | Shariq Refai, MD, MBA",
    description:
      "Press, podcast, speaking, publishing, and editorial collaboration inquiries for Shariq Refai, MD, MBA. Not for medical care, treatment, or crisis communication.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Media & Speaking Inquiries", path: "/media-inquiries" },
    ],
  });
  const faqs = faqSchema([
    {
      question: "What kinds of media inquiries are accepted?",
      answer:
        "Media and press, podcast appearances, speaking engagements, publishing, and editorial collaborations.",
    },
    {
      question: "Is this the right channel for clinical care?",
      answer:
        "No. shariqrefai.com is an educational author and media platform. For clinical care inquiries, visit shrinkmd.com or contact a qualified, licensed clinician of your choosing.",
    },
    {
      question: "How do I submit an inquiry?",
      answer:
        "Use the contact page or email media@shariqrefai.com. Brief context, audience, format, and timing help us route your request quickly.",
    },
  ]);
  return (
    <SiteLayout>
      <SEO
        title="Media & Speaking Inquiries | Shariq Refai, MD, MBA"
        description="Press, podcast, speaking, publishing, and editorial collaboration inquiries for Shariq Refai, MD, MBA. Not for medical care, treatment, or crisis communication."
        url="/media-inquiries"
        schema={[
          personSchema,
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Media & Speaking Inquiries", path: "/media-inquiries" },
          ]),
          faqs,
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Media &amp; Speaking</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Media &amp; Speaking Inquiries
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            Press, podcasts, speaking engagements, publishing, and editorial
            collaborations.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl">
          <div
            role="note"
            aria-label="Important notice for medical and crisis matters"
            className="bg-[#EFECE6] p-6 md:p-8 border-l-4 border-destructive/60 mb-12"
          >
            <h2 className="font-serif text-xl mb-3 text-primary">
              Important: Not for medical care
            </h2>
            <EducationalDisclaimer variant="boxed" className="mb-3" />
            <p className="text-sm font-light text-secondary leading-relaxed mb-3">
              Media inquiries are not clinical communications. This page is
              for editorial, press, and speaking matters only.
              It is <strong>not</strong> for medical care, psychiatric
              treatment, individual clinical questions, prescription
              requests, or submission of personal health information. For
              clinical care inquiries, visit{" "}
              <a
                href={withNetworkUtm("https://shrinkmd.com/", "media_inquiries_page")}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline hover:text-primary"
              >
                shrinkmd.com
              </a>
              .
            </p>
            <p className="text-sm font-medium text-destructive leading-relaxed">
              If you are in crisis or feel unsafe, call or text 988 in the
              United States, call 911, or go to the nearest emergency room.
            </p>
          </div>

          <div className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
            <h2>What this is for</h2>
            <ul>
              <li><strong>Press &amp; commentary</strong>, interviews, expert quotes, on-camera or audio commentary on psychiatry, mental health, technology in healthcare, sports and performance psychiatry, and founder topics.</li>
              <li><strong>Podcast appearances</strong>, long-form conversations on mental health literacy, how thought works, modern psychiatry, and the founder/operator side of building in healthcare.</li>
              <li><strong>Speaking engagements</strong>, keynotes, panels, fireside chats, and corporate or conference talks for healthcare, technology, sports, leadership, and education audiences.</li>
              <li><strong>Publishing</strong>, guest essays, op-eds, contributed pieces, and editorial collaborations.</li>
              <li><strong>Professional collaborations</strong>, academic, industry, and creative partnerships consistent with the educational and editorial nature of this platform.</li>
            </ul>

            <h2>What this is not for</h2>
            <ul>
              <li>Patient intake, appointment requests, or scheduling clinical care.</li>
              <li>Individual symptom questions, second opinions, or treatment recommendations.</li>
              <li>Prescription requests, refills, or coverage questions.</li>
              <li>Submission of medical records, lab results, or any personal health information.</li>
              <li>Crisis intervention. This site is not monitored for emergencies.</li>
            </ul>

            <h2>Topic areas</h2>
            <p>
              Frequent topics include: anxiety and overthinking; how
              "thinking" actually works; modern psychiatry and what good care
              looks like; technology, AI, and the future of mental health
              services; high performers and pressure; sports and performance
              psychiatry; founder mental health; and mental health literacy
              for general audiences.
            </p>

            <h2>How to reach out</h2>
            <p>
              Use the <Link href="/contact">contact page</Link> or email{" "}
              <a href="mailto:media@shariqrefai.com">media@shariqrefai.com</a>, and select
              the inquiry type that best fits, Media &amp; Press, Speaking
              Engagement, Publishing, or Professional Collaboration. Brief
              context, audience, format, and timing help us route your
              request quickly. Due to volume, we may only be able to reply to
              relevant professional requests.
            </p>

            <h2>Bio &amp; usage</h2>
            <p>
              For approved bio language, headshots, and book imagery, please
              note your intended use (publication, episode, event,
              territory, and date) in your message. Materials are licensed
              for the specific use you describe; see the{" "}
              <Link href="/copyright">Copyright &amp; Intellectual Property</Link>{" "}
              page for general terms.
            </p>
          </div>
        </div>
      </article>
      <RelatedPages
        entries={[
          { href: "/media", label: "Press & media resources", why: "Bios, headshots, quote library, and interview requests." },
          { href: "/press", label: "Press coverage & media features", why: "Recent interviews, features, and appearances." },
          { href: "/speaking", label: "Keynote speaker Shariq Refai", why: "Topics, formats, and speaking availability." },
          { href: "/about", label: "About Shariq Refai, MD, MBA", why: "Credentials and background for reporters." },
        ]}
      />
    </SiteLayout>
  );
}
